/**
 * Phase 6c PR 2 — `<SeasonGlance />` — couples `/v2/today` to the 6d Plan
 * surface.
 *
 * Hoisted to `src/components/season/` (per direction.md §8 open question
 * #1, leaning #2) so `/v2/plan` can reuse the rail variant verbatim in a
 * later PR if useful.
 *
 * Two display variants:
 *   - `strip` — 7-cell horizontal week strip, mode-coloured top rule,
 *               current week outlined in `--today`, optional meet flags
 *               above the strip. Lives in the main column on
 *               mobile / comfortable / default / wide.
 *   - `rail`  — 5-row vertical phase list grouped by mode, current phase
 *               highlighted with `--today-soft`, week count on the right.
 *               Lives in the right rail at default + wide.
 *
 * Strip has an `empty` mode for State D (season but no plan): same N
 * cells with dashed `--ink-200` borders + `--ink-100` fill, header
 * surfaces a `Generate plan ›` link instead of the countdown.
 *
 * **Data input:** raw `MacroPlanWeek[]` from the existing 6d
 * `useMacroPlan()` hook. The component derives phase groups via
 * `groupPhases()` — same helper the Plan surface uses. No new contract.
 *
 * Sourced from
 * `.claude/references/phase-6c-dashboard-design/mockups/styles.css`
 * §"SeasonGlance" + `direction.md §3.2`.
 */

import type { MacroPlanWeek } from '../../hooks/useMacroPlan';
import { formatWeekRange } from '../../lib/formatWeekRange';
import { groupPhases } from '../../lib/macroPlanPhases';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './SeasonGlance.module.css';

export type SeasonPhaseId = 'base' | 'build' | 'competition' | 'taper' | 'recovery';

const KNOWN_PHASES: ReadonlySet<string> = new Set([
  'base',
  'build',
  'competition',
  'taper',
  'recovery',
]);

const PHASE_LABEL: Record<SeasonPhaseId, string> = {
  base: 'Base',
  build: 'Build',
  competition: 'Competition',
  taper: 'Taper',
  recovery: 'Recovery',
};

const PHASE_CLASS: Record<SeasonPhaseId, string> = {
  base: styles.cellBase,
  build: styles.cellBuild,
  competition: styles.cellCompetition,
  taper: styles.cellTaper,
  recovery: styles.cellRecovery,
};

const PHASE_ROW_CLASS: Record<SeasonPhaseId, string> = {
  base: styles.phaseRowBase,
  build: styles.phaseRowBuild,
  competition: styles.phaseRowCompetition,
  taper: styles.phaseRowTaper,
  recovery: styles.phaseRowRecovery,
};

function asPhaseId(mode: string): SeasonPhaseId | null {
  return KNOWN_PHASES.has(mode) ? (mode as SeasonPhaseId) : null;
}

export type StripVariantProps = {
  display: 'strip';
  /** Required when `empty` is false. Sorted by week_number ASC. */
  weeks?: MacroPlanWeek[];
  /** State D — render N dashed cells in place of real weeks. */
  empty?: boolean;
  /** How many empty cells to render in `empty` mode. Default 7. */
  emptyWeekCount?: number;
  /** "Spring 2026 XC" — shown in row1.kicker. */
  seasonName?: string;
  /** "4 wks to State Meet · entered Competition Mon". Hidden when empty. */
  countdown?: string;
  /** Override current-week selection. Otherwise inferred from
   *  `currentWeekFromDates(weeks)` — but caller usually passes this
   *  explicitly to match the dashboard's "Today" framing. */
  currentWeek?: number | null;
  /** "Open season plan ›" link in row1. */
  openLink?: { label: string; href: string };
  /** Empty-variant CTA — replaces countdown with "Generate plan ›". */
  generatePlanHref?: string;
  /** Render each cell as <a> when provided; otherwise plain divs. */
  weekHref?: (weekNumber: number) => string;
  /** Optional meet labels per week_number — surface as flags above the
   *  cell. Caller usually maps from `meet_id` → meet display name via
   *  a separate hook. When omitted, no flags render. */
  meetLabels?: Record<number, string>;
  /** Season-model Phase 3 — the set of week_numbers that have a DRAFTED
   *  weekly plan (from `useDraftedWeeks` / `draftedWeeksFromPlans`). When
   *  provided, each cell is marked drafted (solid + filled dot) vs undrafted
   *  (dashed + hollow dot). When OMITTED (e.g. the demo showcase), cells
   *  render exactly as before — no marking. Navigation is unchanged either
   *  way: every cell still links to its week. */
  draftedWeeks?: Set<number>;
};

export type RailVariantProps = {
  display: 'rail';
  weeks: MacroPlanWeek[];
  seasonName?: string;
  currentWeek?: number | null;
  openLink?: { label: string; href: string };
};

export type SeasonGlanceProps = StripVariantProps | RailVariantProps;

export function SeasonGlance(props: SeasonGlanceProps) {
  if (props.display === 'rail') return <SeasonGlanceRail {...props} />;
  return <SeasonGlanceStrip {...props} />;
}

// ─── Strip ────────────────────────────────────────────────────────

function SeasonGlanceStrip(props: StripVariantProps) {
  const iconsOn = useIconSystem();
  const { empty = false, emptyWeekCount = 7 } = props;
  const weeks = empty ? [] : props.weeks ?? [];
  const totalWeeks = weeks.length;

  // Default to the latest known week_number when caller doesn't override.
  // (For the dashboard, callers typically pass currentWeek explicitly to
  // match the "Today" determination.)
  const currentWeek = props.currentWeek ?? null;

  const meetLabels = props.meetLabels ?? {};
  // Season-model Phase 3 — when the caller passes the drafted set, mark each
  // cell drafted/undrafted; when omitted, `draftKnown` is false and cells
  // render unchanged (demo showcase / callers that don't have the set).
  const draftedWeeks = props.draftedWeeks;
  const draftKnown = draftedWeeks != null;

  return (
    <section
      className={`${styles.seasonGlance} ${empty ? styles.empty : ''}`}
      data-display="strip"
      aria-label={props.seasonName ? `Season glance · ${props.seasonName}` : 'Season glance'}
    >
      <div className={styles.row1}>
        {props.seasonName && (
          <span className={styles.kicker}>Season · {props.seasonName}</span>
        )}
        {!empty && props.countdown && (
          <span className={styles.countdown}>{props.countdown}</span>
        )}
        {empty && props.generatePlanHref && (
          <a className={styles.openLink} href={props.generatePlanHref}>
            Generate plan ›
          </a>
        )}
        {!empty && props.openLink && (
          <a className={styles.openLink} href={props.openLink.href}>
            {props.openLink.label} ›
          </a>
        )}
      </div>

      <div className={styles.weekDots}>
        {empty
          ? renderEmptyCells(emptyWeekCount)
          : weeks.map((w, idx) => {
              const isCurrent = currentWeek != null && w.week_number === currentWeek;
              const isPast =
                currentWeek != null && w.week_number < currentWeek;
              const phaseId = asPhaseId(w.mode);
              const endIso = endOfWeekIso(w.week_start_date);
              // B87 leftovers (2026-09-08) — one joiner for a week range, the
              // word, matching `useWeeklyPlan`'s Today kicker 35 lines below
              // this strip on the same screen (the register carries no dash).
              // UX round 4 D-A (2026-09-09): the range now comes from the ONE
              // rule, `lib/formatWeekRange` — the month is printed once when
              // both dates share it ("Apr 6 to 12") and twice only when they
              // differ ("Apr 27 to May 3"), so the strip cell reads the same
              // way as the kicker below it and the chat save banner.
              const dateLabel = formatWeekRange(w.week_start_date, endIso) ?? '—';
              const wnLabel = isCurrent
                ? `W${w.week_number} · Now`
                : `W${w.week_number}${phaseId ? ` · ${PHASE_LABEL[phaseId]}` : ''}`;
              const meet = meetLabels[w.week_number];
              const isDrafted = draftedWeeks?.has(w.week_number) ?? false;
              const cellClasses = [
                styles.weekCell,
                phaseId ? PHASE_CLASS[phaseId] : '',
                isCurrent ? styles.cellCurrent : '',
                isPast ? styles.cellPast : '',
                draftKnown && !isDrafted ? styles.cellUndrafted : '',
              ]
                .filter(Boolean)
                .join(' ');
              // Status cue only when the caller passed the drafted set.
              const cellAria = draftKnown
                ? `Week ${w.week_number}${phaseId ? `, ${PHASE_LABEL[phaseId]}` : ''}, ${
                    isDrafted ? 'drafted' : 'not drafted yet'
                  }`
                : undefined;
              const cellInner = (
                <>
                  {draftKnown ? (
                    <span
                      className={styles.draftDot}
                      data-drafted={isDrafted || undefined}
                      aria-hidden
                    />
                  ) : null}
                  <span className={styles.wn}>{wnLabel}</span>
                  <span className={styles.wd}>{dateLabel}</span>
                </>
              );
              // A2 — `mark.meet` at the 9px MICRO mount (ratification: "at the
              // 9px CSS micro mount ship flag-only, no pole").
              //
              // ⚠️ The legacy mark is a CSS `content: '◆'` on `.meetFlag::before`,
              // which no registry call can replace — CSS `content` cannot hold a
              // path, and a data-URI would hardcode the colour the whole system
              // forbids. So the glyph is added INSIDE the span (a real element,
              // already in the DOM) and the pseudo-element is suppressed by a
              // `data-icons` attribute that exists ONLY on the flag-ON path.
              // Exactly the technique A1 §3 used to retire `.active::before` on
              // the tab bar, and it keeps flag-OFF attribute-for-attribute today's.
              const meetFlag = meet ? (
                <span
                  className={styles.meetFlag}
                  data-icons={iconsOn || undefined}
                  style={{ left: `${((idx + 0.5) / totalWeeks) * 100}%` }}
                >
                  {iconsOn ? <Icon name="mark.meet" size={9} /> : null}
                  {meet}
                </span>
              ) : null;
              const href = props.weekHref?.(w.week_number);
              if (href) {
                return (
                  <span key={w.week_number} className={styles.cellWrap}>
                    {meetFlag}
                    <a className={cellClasses} href={href} aria-label={cellAria}>
                      {cellInner}
                    </a>
                  </span>
                );
              }
              return (
                <span key={w.week_number} className={styles.cellWrap}>
                  {meetFlag}
                  <div className={cellClasses} aria-label={cellAria}>
                    {cellInner}
                  </div>
                </span>
              );
            })}
      </div>
    </section>
  );
}

function renderEmptyCells(count: number) {
  return Array.from({ length: count }, (_, i) => {
    const weekNum = i + 1;
    return (
      <span key={weekNum} className={styles.cellWrap}>
        <div className={`${styles.weekCell} ${styles.cellEmpty}`}>
          <span className={styles.wn}>W{weekNum}</span>
          <span className={styles.wd}>—</span>
        </div>
      </span>
    );
  });
}

function endOfWeekIso(startIso: string | null): string | null {
  if (!startIso) return null;
  const [y, m, d] = startIso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const start = new Date(y, m - 1, d);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  const yyyy = end.getFullYear();
  const mm = String(end.getMonth() + 1).padStart(2, '0');
  const dd = String(end.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ─── Rail ─────────────────────────────────────────────────────────

function SeasonGlanceRail(props: RailVariantProps) {
  const phases = groupPhases(props.weeks);
  const currentWeek = props.currentWeek ?? null;

  return (
    <section
      className={`${styles.seasonGlance} ${styles.rail}`}
      data-display="rail"
      aria-label={
        props.seasonName ? `Season glance · ${props.seasonName}` : 'Season glance'
      }
    >
      <div className={styles.row1}>
        {props.seasonName && (
          <span className={styles.kicker}>Season · {props.seasonName}</span>
        )}
        {props.openLink && (
          <a className={styles.openLink} href={props.openLink.href}>
            {props.openLink.label} ›
          </a>
        )}
      </div>

      <div className={styles.phaseList}>
        {phases.map((phase) => {
          const phaseId = asPhaseId(phase.mode);
          if (!phaseId) return null;
          const isCurrent =
            currentWeek != null &&
            currentWeek >= phase.startWeek &&
            currentWeek <= phase.endWeek;
          const isPast =
            currentWeek != null && phase.endWeek < currentWeek;
          const wkCount = phase.endWeek - phase.startWeek + 1;
          const name =
            isCurrent && currentWeek != null
              ? `${PHASE_LABEL[phaseId]} · W${currentWeek}`
              : PHASE_LABEL[phaseId];
          const rowClasses = [
            styles.phaseRow,
            PHASE_ROW_CLASS[phaseId],
            isCurrent ? styles.phaseRowCurrent : '',
            isPast ? styles.phaseRowPast : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div
              key={`${phaseId}-${phase.startWeek}`}
              className={rowClasses}
              data-mode={phaseId}
              data-current={isCurrent || undefined}
            >
              <div className={styles.rule} aria-hidden />
              <div className={styles.name}>{name}</div>
              <div className={styles.wks}>
                {wkCount} {wkCount === 1 ? 'wk' : 'wks'}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default SeasonGlance;
